import { useState } from 'react';

export interface Monster {
  name: string;
  attribute: string;
}

export interface DefenseTeam {
  team: Monster[];
  source: string; // 'db'
  counter_count?: number;
}

/** 무엇으로 찾을지. def = 방덱으로, atk = 공덱으로 */
export type SearchSide = 'def' | 'atk';

export interface CounterListState {
  searchSide: SearchSide;
  setSearchSide: (side: SearchSide) => void;
  selectedMonsters: any[];
  setSelectedMonsters: React.Dispatch<React.SetStateAction<any[]>>;
  counterModalOpen: number | null;
  setCounterModalOpen: React.Dispatch<React.SetStateAction<number | null>>;
  counterFilteredTeams: DefenseTeam[];
  setCounterFilteredTeams: React.Dispatch<React.SetStateAction<DefenseTeam[]>>;
  selectedTeamIdx: number | null;
  setSelectedTeamIdx: React.Dispatch<React.SetStateAction<number | null>>;
  counterList: { counters: any[] } | null;
  setCounterList: React.Dispatch<React.SetStateAction<{ counters: any[] } | null>>;
  loadingCounter: boolean;
  setLoadingCounter: React.Dispatch<React.SetStateAction<boolean>>;
  handleSearch: () => Promise<void>;
  handleSelectTeam: (idx: number) => Promise<void>;
}

export function useCounter(): CounterListState {
  /**
   * 방덱으로 찾을지 공덱으로 찾을지.
   *
   *   def — 고른 몹이 든 **상대 방덱**을 찾고, 고르면 그걸 뚫은 공덱들을 보여 준다
   *   atk — 고른 몹이 든 **우리 공덱**을 찾고, 고르면 그 공덱이 상대한 방덱들을 보여 준다
   *
   * 서버는 두 경우를 같은 두 엔드포인트로 처리한다(`side` 만 다르다).
   */
  const [searchSide, setSearchSideRaw] = useState<SearchSide>('def');
  const [selectedMonsters, setSelectedMonsters] = useState<any[]>([]);
  const [counterModalOpen, setCounterModalOpen] = useState<number | null>(null);
  const [counterFilteredTeams, setCounterFilteredTeams] = useState<DefenseTeam[]>([]);
  const [selectedTeamIdx, setSelectedTeamIdx] = useState<number | null>(null);
  const [counterList, setCounterList] = useState<{ counters: any[] } | null>(null);
  const [loadingCounter, setLoadingCounter] = useState(false);

  /** 찾는 쪽을 바꾸면 앞선 결과는 뜻이 달라지므로 비운다 */
  const setSearchSide = (side: SearchSide) => {
    setSearchSideRaw(side);
    setCounterFilteredTeams([]);
    setSelectedTeamIdx(null);
    setCounterList(null);
  };

  // 검색 버튼 클릭 시
  const handleSearch = async () => {
    setCounterList(null); // 검색 시작 시 기존 카운터 목록 초기화
    if (selectedMonsters.length === 0) {
      setCounterFilteredTeams([]);
      return;
    }
    setLoadingCounter(true);
    try {
      const res = await fetch('/api/posts/search-def', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          monsters: selectedMonsters.map(m => ({ name: m.name, attribute: m.attribute })),
          side: searchSide
        })
      });
      const data = await res.json();
      setCounterFilteredTeams(data.defenseTeams || []);
    } finally {
      setLoadingCounter(false);
    }
  };

  // 방덱 클릭 시 카운터 검색
  const handleSelectTeam = async (idx: number) => {
    setSelectedTeamIdx(idx);
    setCounterList(null);
    if (!counterFilteredTeams[idx]) return;
    setLoadingCounter(true);
    try {
      const res = await fetch('/api/posts/search-counter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ monsters: counterFilteredTeams[idx].team, side: searchSide })
      });
      const data = await res.json();
      setCounterList(data);
    } catch (e) {
      setCounterList({ counters: [] });
    }
    setLoadingCounter(false);
  };

  return {
    searchSide,
    setSearchSide,
    selectedMonsters,
    setSelectedMonsters,
    counterModalOpen,
    setCounterModalOpen,
    counterFilteredTeams,
    setCounterFilteredTeams,
    selectedTeamIdx,
    setSelectedTeamIdx,
    counterList,
    setCounterList,
    loadingCounter,
    setLoadingCounter,
    handleSearch,
    handleSelectTeam,
  };
} 